import { api } from "./client";

export type Pricing = {
  creator_verification: number | null;
  work_certification: number | null;
  free_works: number | null;
};

// Both fields are always present together (DB CHECK ck_referral_terms_non_negative
// requires both >= 0; there is no independently-nullable field like Pricing has).
// null (at the EventRow/CreatorDetail level) means "no override at this scope" —
// the resolve_referral_terms cascade falls through to the next tier. 0 is a valid,
// deliberate "grants nothing" value, distinct from null — never conflate the two.
export type ReferralTerms = {
  referrer_credits: number;
  referee_bonus_works: number;
};

/** What the cascade actually resolves to for one creator, and from where.
 * `source` is "individual" for a tier-1 creator deal, else "<origin>/<event>".
 * Null overall when nothing is configured anywhere in the cascade. */
export type EffectiveReferralTerms = ReferralTerms & {
  source: string;
};

export type EventRow = {
  id: string;
  origin_id: string;
  origin_slug: string | null;
  display_name: string | null;
  name: string | null;
  slug: string | null;
  event_date: string | null;
  event_end_date: string | null;
  house_default_start_at: string | null;
  house_default_end_at: string | null;
  location: string | null;
  created_at: string | null;
  updated_at: string | null;
  campaign_url: string | null;
  short_url: string | null;
  pricing: Pricing | null;
  effective_pricing: Pricing | null;
  pricing_inherited: boolean;
  referral_terms: ReferralTerms | null;
  is_general_default: boolean;
};

export type EventListResponse = {
  events: EventRow[];
  page: number;
  page_size: number;
  total: number;
};

export type EventSortField = "created_at" | "event_date" | "event_end_date" | "name";
export type SortDir = "asc" | "desc";

export type EventListParams = {
  page: number;
  page_size?: number;
  q?: string;
  origin_id?: string;
  sort?: EventSortField;
  dir?: SortDir;
};

export type EventDetail = EventRow;

export type EventPatch = Partial<{
  origin_id: string;
  name: string | null;
  slug: string | null;
  event_date: string | null;
  event_end_date: string | null;
  house_default_start_at: string | null;
  house_default_end_at: string | null;
  location: string | null;
  pricing: Pricing | null;
  referral_terms: ReferralTerms | null;
}>;

export type EventCreate = {
  origin_id: string;
  name: string | null;
  slug?: string | null;
  event_date: string | null;
  event_end_date?: string | null;
  location: string | null;
  pricing?: Pricing;
};

export const EventsApi = {
  list(params: EventListParams) {
    return api
      .get<EventListResponse>("/events", {
        params: {
          page: params.page,
          page_size: params.page_size ?? 40,
          q: params.q || undefined,
          origin_id: params.origin_id || undefined,
          sort: params.sort || undefined,
          dir: params.dir || undefined,
        },
      })
      .then((r) => r.data);
  },
  get(id: string) {
    return api.get<EventDetail>(`/events/${id}`).then((r) => r.data);
  },
  create(body: EventCreate) {
    return api.post<EventDetail>("/events", body).then((r) => r.data);
  },
  update(id: string, patch: EventPatch) {
    return api.patch<EventDetail>(`/events/${id}`, patch).then((r) => r.data);
  },
  bulkDelete(ids: string[]) {
    return api
      .delete<{ deleted: string[]; refused: string[] }>("/events", {
        data: { ids },
      })
      .then((r) => r.data);
  },
};
